import * as mutation from "./mutation-types";
import * as action from "./action-types";
import * as systemLogMutation from "@/store/system-logs/mutation-types";
import { SHOW_NOTIFICATION } from "@/store/notifications/mutation-types";
import { GLOBAL_ERROR } from "@/store/system/mutation-types";
import { tableHelper } from "@/helpers/table.helper";
import { deepCopyAxiosException } from "@/store/helpers";
import axios from "axios";

export const actions = {
  async [action.LIST]({ commit, state }) {
    commit(mutation.LIST_STARTED);
    try {
      const url = tableHelper.constructUrl(
        `/api/levy-collections/${state.listing.levyCollectionId}/notice-templates`,
        state.listing.paging
      );
      const { headers, data } = await axios.get(url);
      commit(mutation.LIST_SET_CRITERIA_DESCRIPTION, tableHelper.getCriteriaInfo(headers));
      commit(mutation.LIST_SET_PAGING, tableHelper.getPagingFromHeaders(headers));
      commit(mutation.LIST_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.LIST_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.LIST_NEXT_PAGE]({ state, commit, dispatch }) {
    commit(mutation.LIST_SET_PAGING, { page: state.listing.paging.page + 1 });
    await dispatch(action.LIST);
  },
  async [action.LIST_PREVIOUS_PAGE]({ state, commit, dispatch }) {
    commit(mutation.LIST_SET_PAGING, { page: state.listing.paging.page - 1 });
    await dispatch(action.LIST);
  },

  async [action.EXPORT_SPREADSHEET]({ commit, state }) {
    commit(mutation.EXPORT_SPREADSHEET_STARTED);
    try {
      commit(SHOW_NOTIFICATION, "Your export will commence shortly.");
      const url = tableHelper.constructUrl(
        `/api/exports/levy-collections/${state.listing.levyCollectionId}/notice-templates`,
        state.listing.criteria
      );
      tableHelper.downloadExportedSpreadsheet(url);
      commit(mutation.EXPORT_SPREADSHEET_SUCCEEDED);
      return true;
    } catch (e) {
      commit(mutation.EXPORT_SPREADSHEET_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.EXPORT_JSON]({ commit, state }) {
    commit(mutation.EXPORT_JSON_STARTED);
    try {
      commit(SHOW_NOTIFICATION, "Your export will commence shortly.");
      const url = `/api/exports/levy-collections/${state.listing.levyCollectionId}/notice-templates?asJson=true`;
      tableHelper.downloadExportedSpreadsheet(url);
      commit(mutation.EXPORT_JSON_SUCCEEDED);
      return true;
    } catch (e) {
      commit(mutation.EXPORT_JSON_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.VIEW]({ commit, state }, { levyCollectionId, noticeType }) {
    commit(mutation.VIEW_STARTED, { levyCollectionId, noticeType });
    try {
      const { data } = await axios.get(`/api/levy-collections/${levyCollectionId}/notice-templates/${noticeType}`);
      commit(mutation.VIEW_SUCCEEDED, data);
      const index = tableHelper.getIndexOfId(state.listing, state.view.data.id);
      const nextRecord = tableHelper.getNextRecordInfo(index, state.listing, "type");
      const previousRecord = tableHelper.getPreviousRecordInfo(index, state.listing, "type");
      commit(mutation.VIEW_NEXT, nextRecord);
      commit(mutation.VIEW_PREVIOUS, previousRecord);
      return true;
    } catch (e) {
      commit(mutation.VIEW_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.VIEW_REFRESH]({ state, dispatch }, { levyCollectionId, noticeType }) {
    const needsRefreshed =
      (!state.view.loaded && !state.view.loading) ||
      state.view.levyCollectionId != levyCollectionId ||
      state.view.noticeType != noticeType;
    if (needsRefreshed) {
      await dispatch(action.VIEW, { levyCollectionId, noticeType });
    }
  },

  async [action.EDIT_GET]({ commit }, { levyCollectionId, noticeType }) {
    commit(mutation.EDIT_GET_STARTED);
    try {
      const { data } = await axios.get(`/api/levy-collections/${levyCollectionId}/notice-templates/${noticeType}`);
      commit(mutation.EDIT_GET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.EDIT_GET_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.EDIT_SAVE]({ commit, state }, { levyCollectionId, type }) {
    commit(mutation.EDIT_SAVE_STARTED, { levyCollectionId, type });
    try {
      let result;
      result = await axios.put(`/api/levy-collections/${levyCollectionId}/notice-templates/${type}`, state.edit.data);
      commit(mutation.EDIT_SAVE_SUCCEEDED, result.data);
      commit(SHOW_NOTIFICATION, `Notice template updated.`);
      commit(mutation.STATE_UNLOAD);
      commit(systemLogMutation.STATE_UNLOAD);
      return true;
    } catch (e) {
      commit(mutation.EDIT_SAVE_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.PREVIEW_GET]({ commit }, { levyCollectionId, noticeType }) {
    commit(mutation.PREVIEW_GET_STARTED, { levyCollectionId, noticeType });
    try {
      const { data } = await axios.get(
        `/api/levy-collections/${levyCollectionId}/notice-templates/${noticeType}/preview`
      );
      commit(mutation.PREVIEW_GET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.PREVIEW_GET_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.PREVIEW_SEND_EMAIL]({ commit }, { levyCollectionId, noticeType }) {
    commit(mutation.PREVIEW_SEND_EMAIL_STARTED);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/notice-templates/${noticeType}/send-email-preview`;
      const { data } = await axios.post(url);
      commit(mutation.PREVIEW_SEND_EMAIL_SUCCEEDED, data);
      commit(SHOW_NOTIFICATION, `A preview of the template has been sent to your email address.`);
      commit(systemLogMutation.STATE_UNLOAD);
      return true;
    } catch (e) {
      commit(mutation.PREVIEW_SEND_EMAIL_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return true;
    }
  },

  async [action.GET_SUBJECT_TAGS]({ commit }, levyCollectionId) {
    commit(mutation.GET_SUBJECT_TAGS_STARTED);
    try {
      const { data } = await axios.get(`/api/levy-collections/${levyCollectionId}/notice-templates/tags/NoticeSubject`);
      commit(mutation.GET_SUBJECT_TAGS_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.GET_SUBJECT_TAGS_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.GET_BODY_TAGS]({ commit }, levyCollectionId) {
    commit(mutation.GET_BODY_TAGS_STARTED);
    try {
      const { data } = await axios.get(`/api/levy-collections/${levyCollectionId}/notice-templates/tags/NoticeBody`);
      commit(mutation.GET_BODY_TAGS_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.GET_BODY_TAGS_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  }
};
